import { useNavigate } from "react-router-dom";
import CustomButton from "../../components/CustomButton";
import PageHeader from "../../components/PageHeader";
import Tabs from "../../components/Tabs";
import PaginationBar from "../../components/PaginationBar";
import useUrlTab from "../../util/hooks/useUrlTab";
import ExamsList from "./ExamList";
import AssignmentList from "../assignments/AssignmentsList";
import { JSX, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import {
  fetchExams,
  setPage as setExamPage,
  setPageSize as setExamPageSize,
} from "../../features/exam/ExamSlice";
import {
  fetchAssignments,
  setPage as setAssignmentPage,
  setPageSize as setAssignmentPageSize,
} from "../../features/assignment/assignmentSlice";
import { useBranchCode } from "../../util/hooks/useBranch";

const tabOptions = ["Exams", "Assignments"];

export default function Exam() {
  const navigate = useNavigate();
  const { activeTab, handleTabChange } = useUrlTab(tabOptions);
  const dispatch = useDispatch<AppDispatch>();

  // Exams state
  const {
    list: examList,
    loading: examLoading,
    total: examTotal,
    page: examPage,
    size: examSize,
  } = useSelector((state: RootState) => state.exams);

  // Assignments state
  const {
    assignments,
    isLoading: assignmentLoading,
    total: assignmentTotal,
    page: assignmentPage,
    size: assignmentSize,
    filters
  } = useSelector((state: RootState) => state.assignment);

  const branchCode = useBranchCode()

  // Fetch data based on active tab & pagination
  useEffect(() => {
    if (activeTab === "Exams") {
      dispatch(fetchExams({ page: examPage, size: examSize }));
    } else if (activeTab === "Assignments") {
      dispatch(
        fetchAssignments({
          branchCode,
          page: assignmentPage,
          size: assignmentSize,
          filters
        })
      );
    }
  }, [
    dispatch,
    activeTab,
    examPage,
    examSize,
    assignmentPage,
    assignmentSize,
    filters,
    branchCode
  ]);

  const tabContent: Record<string, JSX.Element> = {
    Exams: (
      <ExamsList
        list={examList}
        loading={examLoading}
        refreshList={() =>
          dispatch(fetchExams({ page: examPage, size: examSize }))
        }
      />
    ),
    Assignments: (
      <AssignmentList
        list={assignments}
        loading={assignmentLoading}
        refreshList={() =>
          dispatch(
            fetchAssignments({
              branchCode,
              page: assignmentPage,
              size: assignmentSize,
              filters
            })
          )
        }
      />
    ),
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-row justify-between items-start gap-5">
        <PageHeader />
        <div className="flex flex-wrap gap-3 justify-end items-end">
          <CustomButton
            text="Create Assignments"
            color="bg-success-600"
            onClick={() =>
              navigate("/exams-and-assignments/create-assignment")
            }
          />
          <CustomButton
            text="Create Exams"
            color="bg-primary-500"
            onClick={() => navigate("/exams-and-assignments/create-exam")}
          />
        </div>
      </div>

      <div className="flex flex-col md:justify-between gap-2 md:flex-row md:items-center">
        <Tabs
          tabs={tabOptions}
          onTabChange={handleTabChange}
          activeTab={activeTab}
        />

        <PaginationBar
          currentPage={activeTab === "Exams" ? examPage : assignmentPage}
          pageSize={activeTab === "Exams" ? examSize : assignmentSize}
          totalItems={activeTab === "Exams" ? examTotal : assignmentTotal}
          onPageChange={(newPage) =>
            activeTab === "Exams"
              ? dispatch(setExamPage(newPage))
              : dispatch(setAssignmentPage(newPage))
          }
          onPageSizeChange={(newSize) =>
            activeTab === "Exams"
              ? dispatch(setExamPageSize(newSize))
              : dispatch(setAssignmentPageSize(newSize))
          }
        />
      </div>

      {tabContent[activeTab]}
    </div>
  );
}
